import Tag from "@/components/Tag";
import { TABLE_CARD_BODY_CELL, TableCard, TableCardMobileStack, TableCardRow } from "@/components/Table";
import { CardSkeleton } from "@/components/Skeleton";
import EmptyState from "@/components/EmptyState";
import { extendedColors, sysColors } from "@/assets/theme/colors";
import type { CallTopicWorkerStat } from "../queries";
import {
  buildSentimentSplit,
  durationDeltaText,
  formatCount,
  formatDuration,
  formatTotalDuration,
  workerDisplayName,
  workerShareOfMax
} from "../_utils";

type Props = {
  workers: CallTopicWorkerStat[];
  teamAvgDurationSeconds: number | null;
  selectedWorkerName: string | null;
  onSelectWorker: (workerName: string | null) => void;
  loading?: boolean;
};

const SENTIMENT_COLORS: Record<string, string> = {
  positive: extendedColors["extended-success-brand-color"],
  neutral: sysColors["sys-brand-outline"],
  negative: sysColors["sys-brand-error"],
  mixed: sysColors["sys-brand-tertiary"]
};

const SELECTED_ROW_CLASSES = "bg-sys-brand-surface-container-high";

export default function TeamLeaderboard({
  workers,
  teamAvgDurationSeconds,
  selectedWorkerName,
  onSelectWorker,
  loading = false
}: Props) {
  const maxCalls = workers.reduce((max, entry) => Math.max(max, entry.calls), 0);

  return (
    <TableCard title="Team performance" icon="group" testId="call-topic-team-leaderboard">
      {loading && workers.length === 0 ? (
        <CardSkeleton />
      ) : workers.length === 0 ? (
        <EmptyState title="No team activity in range." iconName="group" />
      ) : (
        <table className="w-full" data-testid="call-topic-team-leaderboard-table">
          <tbody>
            {workers.map((worker, index) => {
              const selectable = worker.workerName != null;
              const selected = selectable && worker.workerName === selectedWorkerName;
              const slices = buildSentimentSplit(worker);
              return (
                <TableCardRow
                  key={worker.workerName ?? "unknown"}
                  columnCount={8}
                  className={selected ? SELECTED_ROW_CLASSES : undefined}
                  mobile={
                    <TableCardMobileStack
                      lines={[
                        `${index + 1}. ${workerDisplayName(worker.workerName)} · ${formatCount(worker.calls)} calls`,
                        `Avg ${formatDuration(worker.avgDurationSeconds)} · ${durationDeltaText(worker.avgDurationSeconds, teamAvgDurationSeconds)}`
                      ]}
                    />
                  }
                  onClick={
                    selectable ? () => onSelectWorker(selected ? null : (worker.workerName as string)) : undefined
                  }
                  ariaLabel={
                    selectable
                      ? selected
                        ? `Clear ${worker.workerName} filter`
                        : `Filter dashboard to ${worker.workerName}`
                      : undefined
                  }
                >
                  <td className={`${TABLE_CARD_BODY_CELL} text-sys-brand-on-surface-variant`}>{index + 1}</td>
                  <td className={TABLE_CARD_BODY_CELL}>
                    <span className={selected ? "text-body-medium-semibold" : undefined}>
                      {workerDisplayName(worker.workerName)}
                    </span>
                  </td>
                  <td className={TABLE_CARD_BODY_CELL}>
                    <span className="flex items-center gap-2">
                      <span>{formatCount(worker.calls)}</span>
                      <span
                        className="bg-sys-brand-surface-container-highest h-2 w-16 overflow-hidden rounded-full"
                        role="img"
                        aria-label={`${formatCount(worker.calls)} calls`}
                      >
                        <span
                          className="bg-sys-brand-primary block h-full rounded-full"
                          style={{ width: `${Math.round(workerShareOfMax(worker.calls, maxCalls) * 100)}%` }}
                        />
                      </span>
                    </span>
                  </td>
                  <td className={TABLE_CARD_BODY_CELL}>{formatDuration(worker.avgDurationSeconds)}</td>
                  <td className={`${TABLE_CARD_BODY_CELL} text-sys-brand-on-surface-variant`}>
                    {durationDeltaText(worker.avgDurationSeconds, teamAvgDurationSeconds)}
                  </td>
                  <td className={TABLE_CARD_BODY_CELL}>{formatTotalDuration(worker.totalDurationSeconds)}</td>
                  <td className={TABLE_CARD_BODY_CELL}>
                    {worker.topTopic ? <Tag tagType="secondary">{worker.topTopic}</Tag> : "—"}
                  </td>
                  <td className={TABLE_CARD_BODY_CELL}>
                    {slices.length === 0 ? (
                      "—"
                    ) : (
                      <span
                        className="flex h-2 w-20 overflow-hidden rounded-full"
                        role="img"
                        aria-label={slices.map((slice) => `${slice.label} ${slice.count}`).join(", ")}
                        title={slices.map((slice) => `${slice.label}: ${formatCount(slice.count)}`).join(" · ")}
                      >
                        {slices.map((slice) => (
                          <span
                            key={slice.key}
                            className="block h-full"
                            style={{
                              width: `${slice.share * 100}%`,
                              backgroundColor: SENTIMENT_COLORS[slice.key]
                            }}
                          />
                        ))}
                      </span>
                    )}
                  </td>
                </TableCardRow>
              );
            })}
          </tbody>
        </table>
      )}
    </TableCard>
  );
}
